import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pause, Play } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Seo, SITE_URL } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { ShareTrackButton, WhatsAppTrackButton } from "@/components/ShareTrackButton";
import { SocialIcon } from "@/components/SocialIcons";
import { fmtDate } from "./News";

export default function TrackPage() {
  const { id } = useParams();
  const [track, setTrack] = useState(null);
  const [album, setAlbum] = useState(null);
  const [missing, setMissing] = useState(false);
  const { playTrack, track: current, playing } = usePlayer();

  useEffect(() => {
    Promise.all([api.get("/releases"), api.get("/albums")])
      .then(([r, a]) => {
        const t = r.data.find((x) => x.id === id);
        if (!t) return setMissing(true);
        setTrack(t);
        if (t.album_id) setAlbum(a.data.find((al) => al.id === t.album_id) || null);
      })
      .catch(() => setMissing(true));
  }, [id]);

  if (missing)
    return (
      <div className="pt-40 pb-24 text-center" data-testid="track-missing">
        <p className="text-slate-400">Release not found.</p>
        <Link to="/music" className="text-gold-300 text-sm mt-4 inline-block">Back to music</Link>
      </div>
    );
  if (!track) return <div className="pt-40 pb-24 text-center text-slate-500">Loading…</div>;

  const active = current?.id === track.id;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MusicRecording",
    name: track.title,
    byArtist: { "@type": "MusicGroup", name: "Open Horizon Records" },
    url: `${SITE_URL}/music/${track.id}`,
    image: `${SITE_URL}${track.cover_url}`,
    ...(track.release_date ? { datePublished: track.release_date } : {}),
    ...(album ? { inAlbum: { "@type": "MusicAlbum", name: album.title } } : {}),
    ...(track.spotify_url || track.apple_url
      ? { sameAs: [track.spotify_url, track.apple_url].filter(Boolean) }
      : {}),
  };

  return (
    <div className="pt-28 pb-24" data-testid="track-page">
      <Seo
        title={`${track.title} - Open Horizon Records`}
        description={`${track.title} by Open Horizon Records — ${track.is_single ? "single" : `from the album ${album?.title || ""}`}${track.release_date ? `, ${track.release_date}` : ""}. Listen to a 60-second preview, read the lyrics and stream the full track.`}
        path={`/music/${track.id}`}
        image={track.cover_url ? `${SITE_URL}${track.cover_url}` : undefined}
        jsonLd={jsonLd}
      />
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Link to="/music" data-testid="back-to-music"
            className="inline-flex items-center gap-2 text-sm text-gold-300 hover:text-gold-200 transition-colors mb-10">
            <ArrowLeft size={15} /> All music
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 items-start">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-gold-400/10 blur-3xl" aria-hidden />
              <img src={mediaUrl(track.cover_url)} alt={`${track.title} — original cover artwork, Open Horizon Records`}
                className="relative w-full h-auto aspect-square object-cover rounded-2xl border border-gold-400/30"
                data-testid="track-page-cover" />
            </div>
            <div>
              <p className="eyebrow mb-2">{track.is_single ? "Single" : album ? `From the album ${album.title}` : "Album track"}</p>
              <h1 className="font-serif text-3xl sm:text-5xl text-slate-50 leading-tight" data-testid="track-page-title">{track.title}</h1>
              {track.release_date && <p className="text-sm text-slate-400 mt-2">{fmtDate(track.release_date)}</p>}
              <div className="mt-7 flex items-center gap-4">
                <button onClick={() => playTrack(track)} data-testid="track-page-play"
                  aria-label={`Play ${track.title} preview`}
                  className="w-14 h-14 shrink-0 rounded-full flex items-center justify-center text-navy-950 transition-transform hover:scale-105"
                  style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}>
                  {active && playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-1" />}
                </button>
                <span className="text-[11px] tracking-[0.2em] uppercase text-gold-400/70">60-second preview</span>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                {track.spotify_url && (
                  <a href={track.spotify_url} target="_blank" rel="noopener noreferrer" data-testid="track-page-spotify" className="btn-ghost-gold !px-5 !py-2.5 text-xs">
                    <SocialIcon name="spotify" size={15} /> Listen on Spotify
                  </a>
                )}
                {track.apple_url && (
                  <a href={track.apple_url} target="_blank" rel="noopener noreferrer" data-testid="track-page-apple" className="btn-ghost-gold !px-5 !py-2.5 text-xs">
                    <SocialIcon name="apple" size={15} /> Listen on Apple Music
                  </a>
                )}
                <ShareTrackButton track={track} testid="track-page-share" />
                <WhatsAppTrackButton track={track} testid="track-page-whatsapp" />
              </div>
              {track.about && (
                <p className="mt-8 text-slate-300/90 font-light leading-relaxed" data-testid="track-page-about">{track.about}</p>
              )}
            </div>
          </div>
          {track.lyrics && (
            <div className="mt-16 max-w-2xl">
              <h2 className="font-serif text-2xl text-slate-50 mb-6">Lyrics</h2>
              <p className="whitespace-pre-line text-sm leading-7 text-slate-300 font-light" data-testid="track-page-lyrics">{track.lyrics}</p>
            </div>
          )}
        </Reveal>
      </div>
    </div>
  );
}
